import { Divider, Group, Stack, Text } from '@mantine/core';
import styled from 'styled-components';
import type { EmailTemplateSummaryRes } from '@dealstack/shared/lib/generated';

import type { TemplateVersion } from '../hooks/useTemplateSummaryLabels.types';
import { TemplateStatus } from './TemplateStatus';

interface Props {
	template: EmailTemplateSummaryRes;
	version: TemplateVersion;
	statusLabel: string;
	toggling: boolean;
	readOnly?: boolean;
	onSelect: () => void;
	onToggle: (enabled: boolean) => void;
}

const Card = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
	padding: var(--mantine-spacing-sm);
	border: 1px solid var(--mantine-color-gray-4);
	border-radius: var(--mantine-radius-md);
	background-color: var(--mantine-color-white);
	text-align: left;
	cursor: pointer;

	&:hover {
		border-color: var(--mantine-color-blue-6);
	}
`;

export const EmailTemplateCard = ({
	template,
	version,
	statusLabel,
	toggling,
	readOnly,
	onSelect,
	onToggle,
}: Props) => (
	<Card
		role="button"
		tabIndex={0}
		onClick={onSelect}
		onKeyDown={(event) => {
			if (
				event.target === event.currentTarget &&
				(event.key === 'Enter' || event.key === ' ')
			) {
				event.preventDefault();
				onSelect();
			}
		}}
	>
		<Stack gap={4} style={{ flex: 1 }}>
			<Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
				<Text fw={600} fz="0.8125rem" lh="1.21875rem">
					{template.name}
				</Text>
				<TemplateStatus
					label={statusLabel}
					active={template.enabled}
					disabled={toggling || readOnly}
					labelSide="left"
					onToggle={onToggle}
				/>
			</Group>
			<Text fz="0.75rem" c="gray.9" lh="1.125rem" lineClamp={2}>
				{template.description}
			</Text>
		</Stack>

		<Divider mt={8} mb={8} color="var(--mantine-color-gray-4)" />

		<Text fz="0.75rem" c="gray.9" lh="1.125rem" truncate>
			{version.editor ? `${version.label} · ${version.editor}` : version.label}
		</Text>
	</Card>
);
